import React, { FunctionComponent, useEffect, useState } from "react";
import { IShelfSegment, IShelfSegmentAlbum } from "../../../hooks/shelfSegments";
import { ShelfSegmentAlbumRepresentation } from "../ShelfSegmentAlbumRepresentation";
import styles from "./styles.module.scss";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { ListItemIcon, Menu, MenuItem, Skeleton, Stack, Tooltip, useTheme } from "@mui/material";
import { AlbumUtilities } from "../../../models/AlbumUtilities";
import { useContextMenu } from "../../../hooks/contextMenu";
import { strings } from "../../../localization/LocalizedStrings";
import { IShelving } from "../../../hooks/shelvings";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { useGetPrivateLink } from "../../../hooks/share/useGetPrivateLink";
import { IAlbum } from "../../../hooks/albums";
import { ShelvingUtilities } from "../../../models/ShelvingUtilities";
import { useGetShelfSegment } from "../../../hooks/shelfSegments/useGetShelfSegment";
import { useIsMobile } from "../../../hooks/isMobile";
import { useGetImage } from "../../../hooks/images";
import { ImageCache } from "../../../models/ImageCache";
import { LazyImageAspectFit } from "../../LazyImageAspectFit";
import classNames from "classnames";
import { ShelfSegmentOptionsButton } from "../ShelfSegmentOptionsButton";
import { IShelf } from "../../../hooks/shelves";
import { useOpenWindow } from "../../../hooks/openWindow";
import { Icon, IconType } from "../../../icons";
import { ShelfSegmentStatsDialog } from "../ShelfSegmentStatsDialog";
import { PiperDivider } from "../../PiperDivider";

export const ShelfSegmentRepresentation: FunctionComponent<IShelfSegmentRepresentationProps> = ({
  shelving,
  shelvings,
  setShelvings,
  shelf,
  shelfSegmentId,
  overrideShelfSegment,
  selectedAlbumIds,
  isLoading,
  setIsLoading,
  onShelfSegmentAlbumSelected,
  onShelfSegmentFetched,
  onRemoveAlbumFromShelfSegment,
  shelvingIndex,
  shelfIndex,
  segmentIndex,
  canRemove,
  onModifyShelving,
  onModifyShelf,
  onModifyShelfSegment,
  onFocus,
  onAssignAlbumsToShelfSegmentByFilter,
  showOptions
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const [shelfSegment, setShelfSegment] = useState<IShelfSegment>();
  const [selectedAlbum, setSelectedAlbum] = useState<IShelfSegmentAlbum>();
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number>();
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState<number>();
  const { getShelfSegment } = useGetShelfSegment();
  const { getPrivateLink } = useGetPrivateLink();
  const [isLoadingShelfSegment, setIsLoadingShelfSegment] = useState(false);
  const { getImage } = useGetImage();
  const [image, setImage] = useState();
  const [imageUpdateCount, setImageUpdateCount] = useState(0);
  const [isStatsDialogOpen, setIsStatsDialogOpen] = useState(false);
  const fetchShelfSegment = async () => {
    setIsLoadingShelfSegment(true);
    const { status, body } = await getShelfSegment({ uuid: shelfSegmentId });
    if (StatusCodesHelper.isSuccessful(status)) {
      setShelfSegment(body);
      onShelfSegmentFetched(body, segmentIndex);
    }
    setIsLoadingShelfSegment(false);
  };
  const { openInNewTab } = useOpenWindow();
  const openOnPrivateCollection = async () => {
    if (!selectedAlbum) return;
    setIsLoading(true);
    const { status, body } = await getPrivateLink({ albumId: selectedAlbum.album.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      openInNewTab(body.shareLink);
    } else {
      toast.error(body.message);
    }
    setIsLoading(false);
  };
  const albumDetail = (album: IAlbum) => (
    <div className={styles.tooltipDiv}>
      <p className={styles.tooltipAlbumName}>{album.name}</p>
      <p className={styles.tooltipArtistName}>{album.artist?.name ?? strings.unknown}</p>
      <p className={styles.tooltipSubtitle} style={{ color: theme.palette.primary.subtitle }}>
        {AlbumUtilities.getSubtitle(album)}
      </p>
    </div>
  );
  useEffect(() => {
    if (!overrideShelfSegment.albums) {
      setShelfSegment(undefined);
      fetchShelfSegment().then();
    }
  }, [shelfSegmentId]);
  useEffect(() => {
    if (overrideShelfSegment) {
      setShelfSegment(overrideShelfSegment);
    }
  }, [overrideShelfSegment]);
  const fetchImage = async () => {
    if (!shelfSegment || !shelfSegment.hasImage) return;
    const response = await getImage({
      name: shelfSegment.uuid,
      width: ImageCache.bigShelfSegmentImageWidth,
      height: ImageCache.bigShelfSegmentImageHeight
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  useEffect(() => {
    if (imageUpdateCount > 0) {
      setImage(undefined);
      fetchImage().then();
    } else {
      setImageUpdateCount(state => state + 1);
    }
  }, [shelfSegmentId, shelfSegment?.hasImage, shelfSegment?.thumbnail]);
  return (
    <li
      className={styles.segment}
      key={`li_segment_${shelfSegmentId}`}
      style={{
        border: "5px solid transparent",
        borderImage: `url(${ShelvingUtilities.getThemeImage(shelving.theme)}) 100`,
        flexDirection: shelving.orientation == "horizontal" ? "column" : "row",
        backgroundImage: `url(${ShelvingUtilities.getThemeImage(shelving.theme)})`,
        backgroundColor: "rgba(0,0,0,0.7)",
        backgroundBlendMode: "darken",
        backgroundRepeat: "repeat",
        justifyContent: isLoading ? "center" : "normal",
        height: shelving.orientation == "horizontal" ? "auto" : "100%",
        width: shelving.orientation == "horizontal" ? "100%" : "auto"
      }}
    >
      {shelfSegment?.hasImage && (
        <LazyImageAspectFit
          className={styles.backgroundImage}
          image={image}
          thumbnail={shelfSegment?.thumbnail}
          defaultImage={undefined}
          beforeLoad={fetchImage}
          alt={""}
        />
      )}
      {!isLoadingShelfSegment ? (
        <Droppable
          key={`st_${shelfSegmentId}`}
          droppableId={`shelf_${shelfSegmentId}_${shelvingIndex}_${shelfIndex}_${segmentIndex}`}
          direction={shelving.orientation}
        >
          {provided => (
            <Stack
              className={styles.segment}
              style={{ borderColor: theme.palette.colors?.highlightSpine }}
              direction={shelving.orientation == "horizontal" ? "row" : "column"}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <div
                className={styles.albumsContainer}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  marginTop: "auto",
                  width: shelving.orientation == "horizontal" ? undefined : "100%",
                  height: shelving.orientation == "horizontal" ? "100%" : undefined,
                  minWidth: shelving.orientation == "horizontal" ? "120px" : undefined,
                  minHeight: shelving.orientation == "horizontal" ? undefined : "120px"
                }}
              >
                <Stack
                  className={styles.albumsDiv}
                  style={{
                    width: shelving.orientation == "horizontal" ? undefined : "100%",
                    height: shelving.orientation == "horizontal" ? "100%" : undefined,
                    padding: isMobile
                      ? "0"
                      : shelving.orientation == "horizontal"
                        ? "0 2px 0 2px"
                        : "2px 0 2px 0"
                  }}
                  direction={shelving.orientation == "horizontal" ? "row" : "column"}
                  spacing={0.07}
                >
                  {shelfSegment?.albums
                    ?.sort((a, b) => a.order - b.order)
                    .map((shelfSegmentAlbum, index) => (
                      <Draggable
                        key={`seg_${shelfSegmentId}_${shelfSegmentAlbum.album.uuid}_${index}`}
                        draggableId={`drag_${shelfSegmentId}_${shelfSegmentAlbum.album.uuid}_${shelvingIndex}_${shelfIndex}_${segmentIndex}_${index}`}
                        index={index}
                        isDragDisabled={isLoading}
                      >
                        {provided => (
                          <Tooltip
                            title={albumDetail(shelfSegmentAlbum.album)}
                            disableInteractive
                            followCursor
                          >
                            <div
                              className={
                                shelving.orientation == "horizontal"
                                  ? styles.albumContainerHorizontal
                                  : styles.albumContainerVertical
                              }
                              onContextMenu={event => {
                                setSelectedAlbum(shelfSegmentAlbum);
                                setSelectedSegmentIndex(segmentIndex);
                                setSelectedAlbumIndex(index);
                                handleContextMenu(event);
                              }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => {
                                setSelectedAlbum(shelfSegmentAlbum);
                                setSelectedSegmentIndex(segmentIndex);
                                setSelectedAlbumIndex(index);
                                if (
                                  selectedAlbumIds.length == 1 &&
                                  selectedAlbumIds[0] == shelfSegmentAlbum.album.uuid
                                ) {
                                  onShelfSegmentAlbumSelected(undefined);
                                } else {
                                  onShelfSegmentAlbumSelected(shelfSegmentAlbum);
                                }
                              }}
                            >
                              <ShelfSegmentAlbumRepresentation
                                shelving={shelving}
                                shelfSegmentAlbum={shelfSegmentAlbum}
                                isHighlighted={selectedAlbumIds.includes(
                                  shelfSegmentAlbum.album.uuid
                                )}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </Draggable>
                    ))}
                </Stack>
              </div>
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      ) : (
        <Skeleton animation={"wave"} style={{ height: "100%", width: "100%", transform: "none" }} />
      )}
      <Menu {...menuProps}>
        {selectedAlbum && albumDetail(selectedAlbum.album)}
        <PiperDivider style={{ marginTop: "6px", marginBottom: "4px" }} />
        <MenuItem
          key={"OpenOnPrivateCollection"}
          onClick={async () => {
            handleMenuClose();
            await openOnPrivateCollection();
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FeatureSearch} />
          </ListItemIcon>
          {strings.option_view_detail}
        </MenuItem>
        {canRemove && (
          <MenuItem
            key={"Remove"}
            onClick={() => {
              handleMenuClose();
              onRemoveAlbumFromShelfSegment(
                selectedAlbum,
                selectedSegmentIndex,
                selectedAlbumIndex
              );
            }}
            style={{ color: theme.palette.colors?.remove }}
            dense
          >
            <ListItemIcon>
              <Icon style={{ color: theme.palette.colors?.remove }} type={IconType.DeleteForever} />
            </ListItemIcon>
            {strings.shelf_remove_album}
          </MenuItem>
        )}
      </Menu>
      {showOptions && !isLoadingShelfSegment && (
        <Stack className={styles.hoverButtonsStack} direction={"row"} spacing={1}>
          <ShelfSegmentOptionsButton
            className={classNames(styles.hoverButton, styles.hoverButtonRadius)}
            buttonClassName={styles.hoverButtonRadius}
            style={{ backgroundColor: theme.palette.colors?.counterBackground }}
            shelvings={shelvings}
            setShelvings={setShelvings}
            shelving={shelving}
            shelf={shelf}
            shelfSegment={shelfSegment}
            setIsLoading={setIsLoading}
            onModifyShelf={onModifyShelf}
            onModifyShelfSegment={onModifyShelfSegment}
            onModifyShelving={onModifyShelving}
            onFocus={onFocus}
            onStats={() => setIsStatsDialogOpen(true)}
            onAssignAlbumsToShelfSegmentByFilter={onAssignAlbumsToShelfSegmentByFilter}
          />
        </Stack>
      )}
      <ShelfSegmentStatsDialog
        open={isStatsDialogOpen}
        setOpen={setIsStatsDialogOpen}
        shelfSegment={shelfSegment}
        shelf={shelf}
        shelving={shelving}
      />
    </li>
  );
};

export interface IShelfSegmentRepresentationProps {
  shelving: IShelving;
  shelvings: IShelving[];
  setShelvings: (shelvings: IShelving[]) => void;
  shelf: IShelf;
  shelfSegmentId: string;
  overrideShelfSegment: IShelfSegment | undefined;
  selectedAlbumIds: string[];
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onShelfSegmentAlbumSelected: (shelfSegmentAlbum: IShelfSegmentAlbum | undefined) => void;
  onRemoveAlbumFromShelfSegment: (
    shelfSegmentAlbum: IShelfSegmentAlbum,
    segmentIndex: number,
    albumIndex: number
  ) => void;
  onShelfSegmentFetched: (shelfSegment: IShelfSegment, segmentIndex: number) => void;
  shelvingIndex: number;
  shelfIndex: number;
  segmentIndex: number;
  canRemove: boolean;
  onModifyShelving: (shelving: IShelving) => void;
  onModifyShelf: (shelf: IShelf) => void;
  onModifyShelfSegment: (shelf: IShelf, segment: IShelfSegment) => void;
  onFocus: (shelf: IShelf, segment: IShelfSegment) => void;
  onAssignAlbumsToShelfSegmentByFilter: (shelfSegment: IShelfSegment) => void;
  showOptions: boolean;
}
